import { useMemo } from "react";
import { Icon } from "@/components/common/Icon";
import { SubagentElapsed } from "@/components/chat/SubagentElapsed";
import { WorkflowProgressDots } from "@/components/chat/WorkflowProgressDots";
import { WorkflowStopButton } from "@/components/chat/WorkflowStopButton";
import {
	hasMeasurableWorkflowElapsed,
	subagentTotalInputTokens,
	workflowFailureText,
	workflowStatusText,
	workflowTitle,
	type ChatSubagentRun,
	type ChatWorkflowRun,
} from "@/lib/chat";
import { formatTokenCount } from "@/lib/format-datetime";
import { COUNT_BADGE_STYLE, DANGER_BADGE_STYLE } from "@/lib/pane-styles";
import { ROW_HOVER_CLASS, ROW_SELECTED_CLASS } from "@/lib/row-styles";
import {
	groupWorkflowNodes,
	type WorkflowNodeEntry,
	type WorkflowPhaseGroup,
} from "@/lib/workflow-tasks";
import { TaskElapsed } from "./SubagentTaskCard";
import { useSectionCollapse } from "./useSectionCollapse";

/**
 * 名前 / モデル / トークン / 時間。右 3 列は固定幅にして、行をまたいで縦に揃える。左ペインは
 * 狭くなりうるので、右 3 列は中身が収まる最小限に留め、余りは名前へ回す。
 */
const NODE_GRID_CLASS =
	"grid grid-cols-[minmax(0,1fr)_minmax(0,6rem)_3rem_4rem] items-center gap-x-2";
const COUNT_FAILURE_STYLE = { ...COUNT_BADGE_STYLE, ...DANGER_BADGE_STYLE };
const HEADING_CLASS = "mb-1 text-section font-semibold tracking-label uppercase text-fg-3";

/**
 * workflow 1 本をカード 1 枚で描く。上にサマリー (見出し・状態・規模・説明)、下に宣言フェーズを
 * 並べ、フェーズを開くと担うノードの一覧が読める。停止操作はカードに 1 つだけ — 配下のノードは
 * workflow ごと止まる (個別に止めたいノードは、選んで右の詳細から止める)。
 */
export function WorkflowTaskCard({
	workflow,
	runs,
	selectedId,
	onSelect,
}: {
	workflow: ChatWorkflowRun;
	runs: ChatSubagentRun[];
	selectedId: string | null;
	onSelect: (id: string) => void;
}) {
	const group = useMemo(() => groupWorkflowNodes(workflow, runs), [workflow, runs]);
	const title = workflowTitle(workflow);
	const failureText = workflowFailureText(workflow);
	const hasPhases = group.phases.some((phase) => phase.title !== null);
	return (
		<div
			data-testid="workflow-task-card"
			data-status={workflow.status}
			className="mx-2 my-1 min-w-0 rounded-4 bg-surface-1 px-3 py-2.5 shadow-ring"
		>
			<div className="flex min-w-0 items-center gap-2">
				<span
					title={title}
					data-tooltip-if-truncated
					className="min-w-0 flex-1 truncate text-ui font-semibold text-fg-1"
				>
					{title}
				</span>
				<WorkflowStopButton run={workflow} />
			</div>
			<div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-caption text-fg-2">
				<span>
					ワークフロー · {workflowStatusText(workflow.status)}
					{hasMeasurableWorkflowElapsed(workflow) && (
						<>
							{" · "}
							<SubagentElapsed run={workflow} />
						</>
					)}
				</span>
				<span>
					{group.nodes.length}エージェント · {formatTokenCount(group.totalTokens)}トークン
				</span>
			</div>
			{/* name の無い古い行は説明が見出しになっているので、言い直さない。 */}
			{title !== workflow.description && (
				<p className="m-0 mt-1.5 wrap-anywhere text-caption leading-body text-fg-1">
					{workflow.description}
				</p>
			)}
			{failureText && (
				<p data-testid="workflow-error" className="m-0 mt-1 text-caption text-danger wrap-anywhere">
					{failureText}
				</p>
			)}
			{hasPhases ? (
				<div className="mt-2.5">
					<div className={HEADING_CLASS}>フェーズ</div>
					{group.phases.map((phase) => (
						<WorkflowPhaseSection
							key={phase.key}
							phase={phase}
							selectedId={selectedId}
							onSelect={onSelect}
						/>
					))}
				</div>
			) : (
				group.nodes.length > 0 && (
					// フェーズを宣言していない workflow に「フェーズ指定なし」の見出しを 1 つ出しても
					// 情報が無い。束ねずにノードをそのまま並べる。
					<div className="mt-2.5">
						<div className={HEADING_CLASS}>ノード</div>
						<WorkflowProgressDots nodes={group.nodes} className="pb-1" />
						<WorkflowNodeTable nodes={group.nodes} selectedId={selectedId} onSelect={onSelect} />
					</div>
				)
			)}
		</div>
	);
}

/**
 * フェーズ 1 つ。見出しの下に進捗の丸を常に出し、開くとノードの一覧が読める。
 * 左ペインは狭いので字下げはせず、開閉の chevron は右端に置いて名前の幅を削らない。
 */
function WorkflowPhaseSection({
	phase,
	selectedId,
	onSelect,
}: {
	phase: WorkflowPhaseGroup;
	selectedId: string | null;
	onSelect: (id: string) => void;
}) {
	const containsSelected = phase.nodes.some((node) => node.run?.id === selectedId);
	const [collapsed, toggle] = useSectionCollapse(
		!(phase.hasActive || containsSelected),
		containsSelected ? selectedId : null,
		phase.hasActive,
	);
	const label = phase.title ?? "フェーズ指定なし";
	return (
		<section data-testid="workflow-phase" data-phase={phase.title ?? ""}>
			{/* 丸の列も見出しの一部として同じボタンに入れる。別要素にすると hover の帯が 1 行目で切れる。 */}
			<button
				type="button"
				data-testid="workflow-phase-toggle"
				data-collapsed={collapsed ? "true" : "false"}
				onClick={toggle}
				className={`flex w-full cursor-pointer flex-col gap-1 rounded-2 border-0 bg-transparent px-1.5 py-1 text-left [font-family:inherit] ${ROW_HOVER_CLASS}`}
			>
				<span className="flex w-full min-w-0 items-center gap-1.5">
					<span
						title={label}
						data-tooltip-if-truncated
						className="min-w-0 flex-1 truncate text-caption text-fg-1"
					>
						{label}
					</span>
					<span
						data-testid="subagent-section-count"
						data-count-failure={phase.hasFailure ? "true" : "false"}
						style={phase.hasFailure ? COUNT_FAILURE_STYLE : COUNT_BADGE_STYLE}
					>
						{phase.completed}/{phase.total}
					</span>
					<Icon name={collapsed ? "chevronR" : "chevronD"} size={11} color="var(--fg-3)" />
				</span>
				<WorkflowProgressDots nodes={phase.nodes} />
			</button>
			{!collapsed && (
				<div className="pb-1">
					<WorkflowNodeTable nodes={phase.nodes} selectedId={selectedId} onSelect={onSelect} />
				</div>
			)}
		</section>
	);
}

/** 名前 / モデル / トークン / 時間の見出し行と、ノードの行。 */
function WorkflowNodeTable({
	nodes,
	selectedId,
	onSelect,
}: {
	nodes: readonly WorkflowNodeEntry[];
	selectedId: string | null;
	onSelect: (id: string) => void;
}) {
	return (
		<div>
			<div className={`${NODE_GRID_CLASS} px-1.5 py-0.5 text-section text-fg-3`}>
				<span className="truncate">エージェント</span>
				<span className="truncate">モデル</span>
				<span className="truncate text-right">トークン</span>
				<span className="truncate text-right">時間</span>
			</div>
			{nodes.map((node) => (
				<WorkflowNodeRow
					key={node.id}
					node={node}
					selected={node.run !== null && node.run.id === selectedId}
					onSelect={() => node.run && onSelect(node.run.id)}
				/>
			))}
		</div>
	);
}

/**
 * ノード 1 行。run 行の無いノードはまだ実行履歴が無いので選べない。状態はフェーズ見出し下の丸が
 * 担うので、行には繰り返さない (狭い左ペインでは名前の幅のほうが惜しい)。
 */
function WorkflowNodeRow({
	node,
	selected,
	onSelect,
}: {
	node: WorkflowNodeEntry;
	selected: boolean;
	onSelect: () => void;
}) {
	const run = node.run;
	return (
		<button
			type="button"
			data-testid="workflow-node-row"
			data-selected={selected ? "true" : "false"}
			disabled={run === null}
			onClick={onSelect}
			className={`${NODE_GRID_CLASS} w-full cursor-pointer rounded-2 border-0 bg-transparent px-1.5 py-1 text-left text-caption text-fg-1 [font-family:inherit] disabled:cursor-default ${selected ? ROW_SELECTED_CLASS : run === null ? "" : ROW_HOVER_CLASS}`}
		>
			{/* 説明は名前に無い情報なので、省略されていなくても名前と一緒に出す。 */}
			<span
				title={node.description ? `${node.id}\n${node.description}` : node.id}
				data-tooltip-if-truncated={node.description ? undefined : true}
				className="min-w-0 truncate"
			>
				{node.id}
			</span>
			<span title={run?.model} data-tooltip-if-truncated className="min-w-0 truncate text-fg-2">
				{run?.model ?? ""}
			</span>
			<span className="text-right text-fg-2">
				{run ? formatTokenCount(subagentTotalInputTokens(run) + run.outputTokens) : ""}
			</span>
			<span className="text-right text-fg-2">{run && <TaskElapsed run={run} />}</span>
		</button>
	);
}
